/** @module @liquidefy/code-editor/ai/ai-generation @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  CodeGenerationType,
  CodeGenerationContext,
  CodeGenerationConstraints,
  CodeGenerationPreferences,
  CodeGenerationRequest,
  CodeExplanation,
  QualityAssessment,
  SecurityAssessment,
  SecurityVulnerability,
  AlternativeCode,
  Position,
  LanguageId,
  Range,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  asLanguageId,
  now,
} from '../../code-editor.js';

// ═══ §2 REQUEST BUILDING — construct CodeGenerationRequest objects ═══

/**
 * Build a full CodeGenerationRequest with all options.
 */
export function buildGenerationRequest(
  type: CodeGenerationType,
  context: CodeGenerationContext,
  constraints?: Partial<CodeGenerationConstraints>,
  preferences?: Partial<CodeGenerationPreferences>,
): CodeGenerationRequest {
  return {
    type,
    context,
    constraints: {
      maxLength: constraints?.maxLength,
      minLength: constraints?.minLength,
      patterns: constraints?.patterns,
      dependencies: constraints?.dependencies,
      performanceTarget: constraints?.performanceTarget,
      securityLevel: constraints?.securityLevel,
    },
    preferences: {
      codeStyle: preferences?.codeStyle ?? 'balanced',
      errorHandling: preferences?.errorHandling ?? 'try-catch',
      testingApproach: preferences?.testingApproach ?? 'unit',
      documentationLevel: preferences?.documentationLevel ?? 'standard',
    },
  };
}

/**
 * Build a completion request.
 */
export function completionRequest(context: CodeGenerationContext): CodeGenerationRequest {
  return buildGenerationRequest('completion', context, { maxLength: 500 });
}

/**
 * Build a function generation request.
 */
export function functionRequest(
  context: CodeGenerationContext,
  _name: string,
  _description: string,
): CodeGenerationRequest {
  return buildGenerationRequest('function', context, undefined, { documentationLevel: 'standard' });
}

/**
 * Build a class generation request.
 */
export function classRequest(
  context: CodeGenerationContext,
  _name: string,
  _description: string,
): CodeGenerationRequest {
  return buildGenerationRequest('class', context, undefined, { documentationLevel: 'comprehensive' });
}

/**
 * Build a test generation request.
 */
export function testRequest(
  context: CodeGenerationContext,
  _targetCode: string,
): CodeGenerationRequest {
  return buildGenerationRequest('test', context, undefined, { testingApproach: 'unit' });
}

/**
 * Build a documentation generation request.
 */
export function documentationRequest(
  context: CodeGenerationContext,
  _targetCode: string,
): CodeGenerationRequest {
  return buildGenerationRequest('documentation', context, undefined, { documentationLevel: 'comprehensive' });
}

/**
 * Build a refactor request.
 */
export function refactorRequest(
  context: CodeGenerationContext,
  _kind: string,
  _targetCode: string,
): CodeGenerationRequest {
  return buildGenerationRequest('refactor', context);
}

/**
 * Build a fix request.
 */
export function fixRequest(
  context: CodeGenerationContext,
  _diagnostic: string,
): CodeGenerationRequest {
  return buildGenerationRequest('fix', context, undefined, { errorHandling: 'result-type' });
}

/**
 * Build an optimize request.
 */
export function optimizeRequest(
  context: CodeGenerationContext,
  _targetCode: string,
): CodeGenerationRequest {
  return buildGenerationRequest('optimize', context, { performanceTarget: 'speed' });
}

/**
 * Build a translate request.
 */
export function translateRequest(
  context: CodeGenerationContext,
  _targetCode: string,
  _targetLanguage: string,
): CodeGenerationRequest {
  return buildGenerationRequest('translate', context);
}

// ═══ §3 CONTEXT BUILDING — assemble CodeGenerationContext ═══

/**
 * Build a CodeGenerationContext from editor state.
 */
export function buildGenerationContext(
  fileUri: string,
  language: LanguageId,
  position: Position,
  content: string,
  options?: {
    readonly selectedText?: string;
    readonly surroundingLines?: number;
    readonly imports?: readonly string[];
    readonly symbols?: readonly string[];
    readonly projectType?: string;
  },
): CodeGenerationContext {
  const lines = options?.surroundingLines ?? 20;
  return {
    fileUri,
    language,
    cursorPosition: position,
    selectedText: options?.selectedText,
    surroundingCode: extractSurroundingCode(content, position, lines),
    imports: options?.imports ? [...options.imports] : extractImports(content, language),
    symbols: options?.symbols ? [...options.symbols] : extractSymbols(content),
    projectType: options?.projectType,
  };
}

/**
 * Extract N lines of code surrounding the cursor position.
 */
export function extractSurroundingCode(content: string, position: Position, lines: number = 20): string {
  const allLines = content.split('\n');
  const start = Math.max(0, position.line - lines);
  const end = Math.min(allLines.length, position.line + lines + 1);
  return allLines.slice(start, end).join('\n');
}

/**
 * Extract import statements from source code.
 */
export function extractImports(content: string, _language: LanguageId): string[] {
  const imports: string[] = [];
  const lines = content.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    // TypeScript/JavaScript imports
    if (trimmed.startsWith('import ')) {
      imports.push(trimmed);
      continue;
    }
    // Python imports
    if (trimmed.startsWith('from ') && trimmed.includes('import')) {
      imports.push(trimmed);
      continue;
    }
    // Rust use statements
    if (trimmed.startsWith('use ') && trimmed.endsWith(';')) {
      imports.push(trimmed);
      continue;
    }
    // Go imports
    if (trimmed.startsWith('"') && imports.length > 0) {
      imports.push(trimmed);
      continue;
    }
    // C/C++ includes
    if (trimmed.startsWith('#include')) {
      imports.push(trimmed);
      continue;
    }
    // Java imports
    if (trimmed.startsWith('import ') && trimmed.endsWith(';')) {
      imports.push(trimmed);
      continue;
    }
  }
  return imports;
}

/**
 * Extract symbol names (function, class, variable declarations) from code.
 */
export function extractSymbols(content: string): string[] {
  const symbols: string[] = [];
  const patterns = [
    /(?:function|const|let|var|class|interface|type|enum)\s+(\w+)/g,
    /(?:def|class)\s+(\w+)/g,
    /(?:fn|struct|enum|trait|impl)\s+(\w+)/g,
    /(?:func)\s+(\w+)/g,
  ];
  for (const pattern of patterns) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(content)) !== null) {
      if (match[1] && !symbols.includes(match[1])) {
        symbols.push(match[1]);
      }
    }
  }
  return symbols;
}

/**
 * Infer project type from files and package manager.
 */
export function inferProjectType(
  files: readonly string[],
  packageManager?: string,
): string {
  const fileSet = new Set(files.map((f) => {
    const parts = f.split('/');
    return parts[parts.length - 1];
  }));

  if (fileSet.has('Cargo.toml')) return 'rust';
  if (fileSet.has('go.mod')) return 'go';
  if (fileSet.has('pyproject.toml') || fileSet.has('setup.py')) return 'python';
  if (fileSet.has('pom.xml') || fileSet.has('build.gradle')) return 'java';
  if (fileSet.has('Package.swift')) return 'swift';
  if (fileSet.has('package.json')) {
    if (packageManager === 'pnpm') return 'node-pnpm';
    if (packageManager === 'yarn') return 'node-yarn';
    return 'node';
  }
  if (fileSet.has('tsconfig.json')) return 'typescript';
  return 'unknown';
}

/**
 * Estimate the context window size in tokens (rough: ~4 chars per token).
 */
export function contextWindowSize(context: CodeGenerationContext): number {
  let chars = context.surroundingCode.length;
  if (context.selectedText) chars += context.selectedText.length;
  chars += context.imports.reduce((sum, imp) => sum + imp.length, 0);
  chars += context.symbols.reduce((sum, sym) => sum + sym.length, 0);
  return Math.ceil(chars / 4);
}

// ═══ §4 QUALITY ASSESSMENT — readability, maintainability, testability ═══

/**
 * Quality metric thresholds.
 */
export const LINE_LENGTH_THRESHOLD = 120;
export const FUNCTION_LENGTH_THRESHOLD = 50;
export const NESTING_DEPTH_THRESHOLD = 4;
export const PARAMETER_COUNT_THRESHOLD = 5;

/**
 * Assess overall code quality.
 */
export function assessQuality(code: string): QualityAssessment {
  const readability = assessReadability(code);
  const maintainability = assessMaintainability(code);
  const testability = assessTestability(code);
  const issues = identifyIssues(code);
  const score = readability * 0.35 + maintainability * 0.35 + testability * 0.3;
  return { score, readability, maintainability, testability, issues };
}

/**
 * Assess code readability (0-1).
 */
export function assessReadability(code: string): number {
  const lines = code.split('\n');
  let score = 1.0;

  // Penalize long lines
  const longLines = lines.filter((l) => l.length > LINE_LENGTH_THRESHOLD).length;
  score -= (longLines / Math.max(lines.length, 1)) * 0.3;

  // Penalize very short variable names (single letter except i, j, k, x, y)
  const singleLetterVars = (code.match(/\b(?:let|const|var)\s+[a-hm-wz]\b/g) ?? []).length;
  score -= singleLetterVars * 0.05;

  // Reward consistent indentation
  const indentStyles = new Set<string>();
  for (const line of lines) {
    if (line.trim().length === 0) continue;
    const match = line.match(/^(\s+)/);
    if (match) {
      const indent = match[1]!;
      if (indent.includes('\t')) indentStyles.add('tab');
      else indentStyles.add(`space-${indent.length % 4 === 0 ? 4 : indent.length % 2 === 0 ? 2 : indent.length}`);
    }
  }
  if (indentStyles.size > 1) score -= 0.1;

  // Reward comments
  const commentLines = lines.filter((l) => {
    const trimmed = l.trim();
    return trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*') || trimmed.startsWith('#');
  }).length;
  const commentRatio = commentLines / Math.max(lines.length, 1);
  if (commentRatio > 0.05 && commentRatio < 0.5) score += 0.05;

  return Math.max(0, Math.min(1, score));
}

/**
 * Assess code maintainability (0-1).
 */
export function assessMaintainability(code: string): number {
  const lines = code.split('\n');
  let score = 1.0;

  // Penalize very long functions
  const functionBodies = findFunctionBodies(code);
  for (const body of functionBodies) {
    const bodyLines = body.split('\n').length;
    if (bodyLines > FUNCTION_LENGTH_THRESHOLD) {
      score -= 0.1;
    }
  }

  // Penalize deep nesting
  let maxNesting = 0;
  let currentNesting = 0;
  for (const line of lines) {
    const opens = (line.match(/[{(]/g) ?? []).length;
    const closes = (line.match(/[})]/g) ?? []).length;
    currentNesting += opens - closes;
    maxNesting = Math.max(maxNesting, currentNesting);
  }
  if (maxNesting > NESTING_DEPTH_THRESHOLD) {
    score -= (maxNesting - NESTING_DEPTH_THRESHOLD) * 0.05;
  }

  // Penalize functions with many parameters
  const paramCounts = code.match(/\(([^)]*)\)/g)?.map((match) => {
    const inner = match.slice(1, -1).trim();
    if (!inner) return 0;
    return inner.split(',').length;
  }) ?? [];
  const overParam = paramCounts.filter((c) => c > PARAMETER_COUNT_THRESHOLD).length;
  score -= overParam * 0.05;

  // Penalize magic numbers
  const magicNumbers = (code.match(/(?<![.\w])(?!0\b|1\b|2\b|-1\b)\d{2,}/g) ?? []).length;
  score -= Math.min(magicNumbers * 0.02, 0.2);

  return Math.max(0, Math.min(1, score));
}

/**
 * Assess code testability (0-1).
 */
export function assessTestability(code: string): number {
  let score = 1.0;

  // Pure functions are more testable
  const hasGlobalMutation = /\bglobal\b|\bwindow\b|\bdocument\b|\bprocess\b/.test(code);
  if (hasGlobalMutation) score -= 0.15;

  // Side effects reduce testability
  const sideEffects = (code.match(/console\.log|fs\.|fetch\(|axios\.|http\./g) ?? []).length;
  score -= Math.min(sideEffects * 0.03, 0.2);

  // Dependency injection improves testability
  const hasDI = /constructor\s*\(|inject|@Inject|createFactory|DI/i.test(code);
  if (hasDI) score += 0.05;

  // Deeply nested logic is hard to test
  let maxNesting = 0;
  let nesting = 0;
  for (const ch of code) {
    if (ch === '{') { nesting++; maxNesting = Math.max(maxNesting, nesting); }
    if (ch === '}') nesting--;
  }
  if (maxNesting > 5) score -= 0.1;

  return Math.max(0, Math.min(1, score));
}

/**
 * Identify specific quality issues in code.
 */
export function identifyIssues(code: string): string[] {
  const issues: string[] = [];
  const lines = code.split('\n');

  const longLines = lines.filter((l) => l.length > LINE_LENGTH_THRESHOLD).length;
  if (longLines > 0) issues.push(`${longLines} line(s) exceed ${LINE_LENGTH_THRESHOLD} characters`);

  const funcBodies = findFunctionBodies(code);
  for (const body of funcBodies) {
    if (body.split('\n').length > FUNCTION_LENGTH_THRESHOLD) {
      issues.push(`Function exceeds ${FUNCTION_LENGTH_THRESHOLD} lines`);
      break;
    }
  }

  if (/var\s+\w/.test(code)) issues.push('Uses var instead of const/let');
  if (/==(?!=)/.test(code)) issues.push('Uses == instead of ===');
  if (/\bany\b/.test(code)) issues.push('Uses any type');
  if (/TODO|FIXME|HACK|XXX/.test(code)) issues.push('Contains TODO/FIXME/HACK comments');
  if (/console\.log/.test(code)) issues.push('Contains console.log statements');

  return issues;
}

function findFunctionBodies(code: string): string[] {
  const bodies: string[] = [];
  const funcPattern = /(?:function\s+\w+|(?:const|let)\s+\w+\s*=\s*(?:async\s*)?\([^)]*\)\s*=>)\s*\{/g;
  let match: RegExpExecArray | null;
  while ((match = funcPattern.exec(code)) !== null) {
    const start = match.index + match[0].length;
    let depth = 1;
    let end = start;
    while (end < code.length && depth > 0) {
      if (code[end] === '{') depth++;
      if (code[end] === '}') depth--;
      end++;
    }
    bodies.push(code.slice(start, end - 1));
  }
  return bodies;
}

// ═══ §5 SECURITY ASSESSMENT — vulnerability detection ═══

/**
 * CWE mapping for common vulnerability types.
 */
export const CWE_MAP: Record<string, string> = {
  'sql-injection': 'CWE-89',
  'xss': 'CWE-79',
  'path-traversal': 'CWE-22',
  'hardcoded-secret': 'CWE-798',
  'unsafe-deserialization': 'CWE-502',
  'command-injection': 'CWE-78',
  'open-redirect': 'CWE-601',
  'insecure-random': 'CWE-330',
  'weak-crypto': 'CWE-327',
  'xxe': 'CWE-611',
};

/**
 * Perform a full security assessment on code.
 */
export function assessSecurity(code: string): SecurityAssessment {
  const vulnerabilities = detectVulnerabilities(code);
  const criticalCount = vulnerabilities.filter((v) => v.severity === 'critical' || v.severity === 'high').length;
  const score = Math.max(0, 1 - criticalCount * 0.2 - vulnerabilities.length * 0.05);
  const recommendations: string[] = [];
  if (vulnerabilities.length > 0) {
    recommendations.push('Review and fix identified vulnerabilities before deployment');
  }
  for (const vuln of vulnerabilities) {
    if (vuln.mitigation) recommendations.push(vuln.mitigation);
  }
  return { score, vulnerabilities, recommendations: [...new Set(recommendations)] };
}

/**
 * Detect all security vulnerabilities in code.
 */
export function detectVulnerabilities(code: string): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];
  vulns.push(...detectSQLInjection(code));
  vulns.push(...detectXSS(code));
  vulns.push(...detectPathTraversal(code));
  vulns.push(...detectHardcodedSecrets(code));
  vulns.push(...detectUnsafeDeserialization(code));
  vulns.push(...detectCommandInjection(code));
  return vulns;
}

/**
 * Detect potential SQL injection vulnerabilities.
 */
export function detectSQLInjection(code: string): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];
  // String concatenation in SQL queries
  const patterns = [
    /(?:query|execute|sql)\s*\(\s*[`'"]\s*(?:SELECT|INSERT|UPDATE|DELETE|DROP)[\s\S]*?\$\{/gi,
    /(?:query|execute|sql)\s*\(\s*(?:\w+\s*\+)/gi,
    /\.(?:raw|unsafeRaw)\s*\(/gi,
  ];
  for (const pattern of patterns) {
    if (pattern.test(code)) {
      vulns.push({
        type: 'sql-injection',
        severity: 'critical',
        description: 'Potential SQL injection via string interpolation or concatenation',
        mitigation: 'Use parameterized queries or prepared statements',
        cweId: CWE_MAP['sql-injection'],
      });
      break;
    }
  }
  return vulns;
}

/**
 * Detect potential XSS vulnerabilities.
 */
export function detectXSS(code: string): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];
  const patterns = [
    /innerHTML\s*=/gi,
    /document\.write\s*\(/gi,
    /\.html\s*\(\s*[^)]*\$\{/gi,
    /dangerouslySetInnerHTML/gi,
    /v-html\s*=/gi,
    /\{@html\s/gi,
  ];
  for (const pattern of patterns) {
    if (pattern.test(code)) {
      vulns.push({
        type: 'xss',
        severity: 'high',
        description: 'Potential cross-site scripting via unsafe HTML rendering',
        mitigation: 'Sanitize user input before rendering as HTML; use text content instead of innerHTML',
        cweId: CWE_MAP['xss'],
      });
      break;
    }
  }
  return vulns;
}

/**
 * Detect potential path traversal vulnerabilities.
 */
export function detectPathTraversal(code: string): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];
  // User input concatenated with file paths
  const patterns = [
    /(?:readFile|writeFile|readdir|unlink|access)\s*\([^)]*\+/gi,
    /path\.(?:join|resolve)\s*\([^)]*(?:req\.|params\.|query\.|body\.)/gi,
    /\.\.\/.*(?:req\.|params\.)/gi,
  ];
  for (const pattern of patterns) {
    if (pattern.test(code)) {
      vulns.push({
        type: 'path-traversal',
        severity: 'high',
        description: 'Potential path traversal via user-controlled file path',
        mitigation: 'Validate and sanitize file paths; use path.normalize and verify resolved path is within allowed directory',
        cweId: CWE_MAP['path-traversal'],
      });
      break;
    }
  }
  return vulns;
}

/**
 * Detect hardcoded secrets in code.
 */
export function detectHardcodedSecrets(code: string): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];
  const patterns = [
    /(?:password|passwd|pwd|secret|api_key|apikey|api_secret|access_token|auth_token|private_key)\s*[:=]\s*['"][^'"]{8,}['"]/gi,
    /(?:AKIA|sk-|sk_live_|rk_live_|ghp_|gho_|ghs_|ghr_|glpat-|xoxb-|xoxp-)\w{10,}/g,
    /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g,
  ];
  for (const pattern of patterns) {
    if (pattern.test(code)) {
      vulns.push({
        type: 'hardcoded-secret',
        severity: 'critical',
        description: 'Hardcoded secret or API key detected',
        mitigation: 'Use environment variables or a secrets manager; never commit secrets to source control',
        cweId: CWE_MAP['hardcoded-secret'],
      });
      break;
    }
  }
  return vulns;
}

/**
 * Detect unsafe deserialization.
 */
export function detectUnsafeDeserialization(code: string): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];
  const patterns = [
    /eval\s*\(/gi,
    /new\s+Function\s*\(/gi,
    /pickle\.loads?\s*\(/gi,
    /yaml\.load\s*\([^)]*(?!Loader)/gi,
    /unserialize\s*\(/gi,
    /JSON\.parse\s*\(\s*(?:req\.|body\.|params\.)/gi,
  ];
  for (const pattern of patterns) {
    if (pattern.test(code)) {
      vulns.push({
        type: 'unsafe-deserialization',
        severity: 'high',
        description: 'Unsafe deserialization or code execution detected',
        mitigation: 'Avoid eval and dynamic code execution; validate and sanitize input before deserialization',
        cweId: CWE_MAP['unsafe-deserialization'],
      });
      break;
    }
  }
  return vulns;
}

/**
 * Detect command injection.
 */
export function detectCommandInjection(code: string): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];
  const patterns = [
    /(?:exec|execSync|spawn|spawnSync|execFile)\s*\([^)]*\$\{/gi,
    /(?:exec|execSync|spawn|spawnSync|execFile)\s*\([^)]*\+\s*(?:req\.|params\.|query\.|body\.|input|user)/gi,
    /child_process.*(?:req\.|params\.|query\.|body\.)/gi,
    /os\.system\s*\(\s*(?:f['"]|.*\+)/gi,
    /subprocess\.(?:call|run|Popen)\s*\([^)]*(?:input|user)/gi,
  ];
  for (const pattern of patterns) {
    if (pattern.test(code)) {
      vulns.push({
        type: 'command-injection',
        severity: 'critical',
        description: 'Potential command injection via user-controlled input',
        mitigation: 'Never pass user input directly to shell commands; use parameterized APIs or allowlists',
        cweId: CWE_MAP['command-injection'],
      });
      break;
    }
  }
  return vulns;
}

// ═══ §6 CODE EXPLANATION — summarize and explain code ═══

/**
 * Generate a code explanation with summary, steps, decisions, and assumptions.
 */
export function explainCode(code: string, _language: string): CodeExplanation {
  const lines = code.split('\n').filter((l) => l.trim().length > 0);
  const symbols = extractSymbols(code);
  const imports = extractImports(code, asLanguageId(''));

  const summary = `Code containing ${lines.length} lines with ${symbols.length} declared symbol(s) and ${imports.length} import(s).`;
  const steps = summarizeSteps(code);
  const designDecisions = extractDesignDecisions(code);
  const assumptions = listAssumptions(code);

  return { summary, steps, designDecisions, assumptions };
}

/**
 * Summarize a function's purpose from its signature and body.
 */
export function summarizeFunction(code: string): string {
  const funcMatch = code.match(/(?:export\s+)?(?:async\s+)?function\s+(\w+)\s*\(([^)]*)\)/);
  if (funcMatch) {
    const name = funcMatch[1]!;
    const params = funcMatch[2]!.trim();
    const paramCount = params ? params.split(',').length : 0;
    return `Function '${name}' takes ${paramCount} parameter(s).`;
  }
  const arrowMatch = code.match(/(?:export\s+)?(?:const|let)\s+(\w+)\s*=\s*(?:async\s*)?\(([^)]*)\)\s*=>/);
  if (arrowMatch) {
    const name = arrowMatch[1]!;
    const params = arrowMatch[2]!.trim();
    const paramCount = params ? params.split(',').length : 0;
    return `Arrow function '${name}' takes ${paramCount} parameter(s).`;
  }
  return 'Function with unrecognized signature.';
}

/**
 * Extract design decisions from code comments and patterns.
 */
export function extractDesignDecisions(code: string): string[] {
  const decisions: string[] = [];

  if (/readonly\s/.test(code)) decisions.push('Uses immutable (readonly) properties');
  if (/Result<|Either<|Option</.test(code)) decisions.push('Uses result/either types for error handling');
  if (/interface\s/.test(code)) decisions.push('Defines contracts via interfaces');
  if (/abstract\s+class/.test(code)) decisions.push('Uses abstract classes for inheritance');
  if (/async\s/.test(code)) decisions.push('Uses async/await for asynchronous operations');
  if (/Map<|Set</.test(code)) decisions.push('Uses Map/Set collections');
  if (/private\s/.test(code)) decisions.push('Encapsulates implementation details');
  if (/generic|<T>|<T,/.test(code)) decisions.push('Uses generics for type safety');
  if (/\bpipe\b|\bcompose\b/.test(code)) decisions.push('Uses functional composition');

  return decisions;
}

/**
 * List assumptions embedded in the code.
 */
export function listAssumptions(code: string): string[] {
  const assumptions: string[] = [];

  if (/!\./.test(code) || /as\s+\w/.test(code)) assumptions.push('Assumes certain values are non-null (uses non-null assertions or type casts)');
  if (/\.length\s*[><=]/.test(code)) assumptions.push('Assumes collections have expected sizes');
  if (/parseInt|parseFloat|Number\(/.test(code)) assumptions.push('Assumes string values are valid numbers');
  if (/JSON\.parse/.test(code)) assumptions.push('Assumes valid JSON input');
  if (/\bfetch\b|\baxios\b|\bhttp\b/.test(code)) assumptions.push('Assumes network availability');
  if (/\.env|process\.env/.test(code)) assumptions.push('Assumes environment variables are configured');

  return assumptions;
}

/**
 * Summarize the complexity characteristics of code.
 */
export function complexityExplanation(code: string): string {
  let maxNesting = 0;
  let nesting = 0;
  for (const ch of code) {
    if (ch === '{') { nesting++; maxNesting = Math.max(maxNesting, nesting); }
    if (ch === '}') nesting--;
  }

  const lines = code.split('\n').filter((l) => l.trim().length > 0);
  const branches = (code.match(/\bif\b|\belse\b|\bswitch\b|\bcase\b|\b\?\b|\bcatch\b/g) ?? []).length;
  const loops = (code.match(/\bfor\b|\bwhile\b|\bdo\b|\.forEach|\.map|\.filter|\.reduce/g) ?? []).length;

  return `${lines.length} lines, ${branches} branch(es), ${loops} loop(s), max nesting depth ${maxNesting}.`;
}

/**
 * Suggest improvements for the code.
 */
export function suggestImprovements(code: string): string[] {
  const suggestions: string[] = [];
  const quality = assessQuality(code);

  if (quality.readability < 0.7) suggestions.push('Improve readability: shorten long lines, use descriptive names');
  if (quality.maintainability < 0.7) suggestions.push('Improve maintainability: extract long functions, reduce nesting');
  if (quality.testability < 0.7) suggestions.push('Improve testability: inject dependencies, avoid global state');
  for (const issue of quality.issues) {
    suggestions.push(`Fix: ${issue}`);
  }

  return suggestions;
}

function summarizeSteps(code: string): string[] {
  const steps: string[] = [];
  const imports = extractImports(code, asLanguageId(''));
  if (imports.length > 0) steps.push(`Import ${imports.length} dependencies`);

  const symbols = extractSymbols(code);
  for (const sym of symbols.slice(0, 5)) {
    steps.push(`Define '${sym}'`);
  }
  if (symbols.length > 5) steps.push(`...and ${symbols.length - 5} more symbols`);

  const exports = (code.match(/\bexport\b/g) ?? []).length;
  if (exports > 0) steps.push(`Export ${exports} public API(s)`);

  return steps;
}

// ═══ §7 ALTERNATIVES — generate structural alternatives ═══

/**
 * Generate N alternative implementations with different tradeoffs.
 */
export function generateAlternatives(code: string, count: number = 3): AlternativeCode[] {
  const alternatives: AlternativeCode[] = [];
  const analysis = tradeoffAnalysis(code);

  // Suggest performance-oriented alternative
  if (count >= 1) {
    alternatives.push({
      code: `// Performance-optimized variant\n${code}`,
      description: 'Performance-focused: minimize allocations, cache results, prefer iterative over recursive',
      tradeoffs: ['Faster execution', 'More complex code', 'Less readable', ...analysis],
    });
  }

  // Suggest readability-oriented alternative
  if (count >= 2) {
    alternatives.push({
      code: `// Readability-optimized variant\n${code}`,
      description: 'Readability-focused: descriptive names, extracted helper functions, comprehensive comments',
      tradeoffs: ['More readable', 'Easier to maintain', 'Slightly more verbose', ...analysis],
    });
  }

  // Suggest functional alternative
  if (count >= 3) {
    alternatives.push({
      code: `// Functional variant\n${code}`,
      description: 'Functional-style: immutable data, pure functions, composition over mutation',
      tradeoffs: ['More testable', 'Referentially transparent', 'May create more intermediate objects', ...analysis],
    });
  }

  return alternatives.slice(0, count);
}

/**
 * Compare the original code with alternatives.
 */
export function compareAlternatives(
  original: string,
  alternatives: readonly AlternativeCode[],
): { readonly originalScore: number; readonly alternativeScores: number[] } {
  const originalScore = assessQuality(original).score;
  const alternativeScores = alternatives.map((alt) => assessQuality(alt.code).score);
  return { originalScore, alternativeScores };
}

/**
 * Analyze the tradeoffs present in existing code.
 */
export function tradeoffAnalysis(code: string): string[] {
  const tradeoffs: string[] = [];

  if (/\bclass\b/.test(code) && /\bextends\b/.test(code)) {
    tradeoffs.push('Uses inheritance (flexible but creates coupling)');
  }
  if (/\binterface\b/.test(code)) {
    tradeoffs.push('Uses interfaces (good abstraction but adds indirection)');
  }
  if (/try\s*\{/.test(code)) {
    tradeoffs.push('Uses try-catch (handles errors but hides control flow)');
  }
  if (/async/.test(code)) {
    tradeoffs.push('Asynchronous (non-blocking but adds complexity)');
  }
  if (/\.map|\.filter|\.reduce/.test(code)) {
    tradeoffs.push('Functional array methods (expressive but create intermediate arrays)');
  }
  if (/for\s*\(/.test(code)) {
    tradeoffs.push('Imperative loops (performant but less expressive)');
  }

  return tradeoffs;
}

/**
 * Suggest common alternative approaches for a generation type.
 */
export function alternativeApproaches(type: CodeGenerationType, _language: string): string[] {
  const approaches: Record<string, string[]> = {
    completion: ['Template-based completion', 'Context-aware suggestion', 'Snippet expansion'],
    function: ['Named function', 'Arrow function', 'Method on class/object', 'Generator function'],
    class: ['Class with constructor', 'Factory function pattern', 'Builder pattern', 'Module pattern'],
    test: ['Arrange-Act-Assert', 'Given-When-Then', 'Table-driven tests', 'Property-based testing'],
    refactor: ['Extract method', 'Inline variable', 'Replace conditional with polymorphism', 'Introduce parameter object'],
    fix: ['Defensive check', 'Type narrowing', 'Error boundary', 'Fallback value'],
    optimize: ['Memoization', 'Lazy evaluation', 'Batch processing', 'Index-based lookup'],
  };
  return approaches[type] ?? ['Standard implementation', 'Alternative pattern'];
}
